import { ItemId, ItemProps } from '@snack-uikit/list';

import { Fn, Predicate } from '@/types/common';
import { NavItemType } from '@/types/navigation';

import { equals, identity, isEmpty, isString } from '../common';
import { skipHashTag } from '../strings';
import { compareUrls, skipFileExtension } from './routes';

type CheckIsActiveTocItemPayload = {
  selectedAnchor: string;
  currentItemID: ItemId;
};

type CheckIsActiveNavItemPayload = {
  url: ItemId;
  pathname: string;
};

type GetActiveElementParentIdsPayload = {
  list: ItemProps[];
  predicate: Predicate<ItemId[]>;
  depthLimit?: number;
  transformParentId?: Fn<ItemId, ItemId>;
};

export const checkIsActiveTocItem = ({ selectedAnchor, currentItemID }: CheckIsActiveTocItemPayload) => {
  const cleanID = isString(currentItemID) ? skipHashTag(currentItemID) : currentItemID;
  return equals(selectedAnchor, cleanID);
};

export const checkIsActiveNavItem = ({ url, pathname }: CheckIsActiveNavItemPayload) =>
  isString(url) ? compareUrls(url, pathname, skipFileExtension('all')) : false;

export function checkIsActiveNavigationItem<T extends NavItemType>(
  itemType: T,
  payload: T extends 'TOC' ? CheckIsActiveTocItemPayload : CheckIsActiveNavItemPayload,
) {
  if (equals(itemType, 'TOC')) return checkIsActiveTocItem(payload as CheckIsActiveTocItemPayload);
  return checkIsActiveNavItem(payload as CheckIsActiveNavItemPayload);
}

export const getActiveElementParentIds = ({
  list,
  predicate,
  depthLimit = Infinity,
  transformParentId = identity,
}: GetActiveElementParentIdsPayload): ItemId[] => {
  const traverse = (items: ItemProps[], currentPath: ItemId[] = [], currentDepth = 0): ItemId[] => {
    if (currentDepth > depthLimit) return [];

    return items.reduce<ItemId[]>((acc, item) => {
      if ('id' in item && item.id && predicate(item.id)) {
        acc.push(...currentPath, transformParentId(item.id));
      }

      if ('items' in item && !isEmpty(item.items)) {
        const id = 'id' in item ? item.id : null;
        const newPath = id ? [...currentPath, transformParentId(id)] : currentPath;
        acc.push(...traverse(item.items, newPath, currentDepth + 1));
      }

      return acc;
    }, []);
  };

  return traverse(list);
};
